/**
 * BobbyProductGallery - Metafield & Variant Filter Compatible
 * Logic: Calculates positions based on :not(.bobby-hidden) elements only.
 */
class BobbyProductGallery {
    constructor(container) {
        this.container = container;
        this.galleryContainer = container.querySelector('.bobby-gallery-container');
        
        // Track state
        this.currentSlide = 0;
        this.isMobile = window.innerWidth < 750;

        // Selectors for elements we need to refresh often
        this.slideSelector = '.bobby-gallery-slide';
        this.thumbSelector = '.bobby-thumb';
        this.dotSelector = '.bobby-dot';

        this.init();
    }

    init() {
        this.updateVisibleElements();
        if (this.visibleSlides.length === 0) return;

        // 1. Handle Navigation Clicks (Thumbs & Dots)
        this.container.addEventListener('click', (e) => {
            const trigger = e.target.closest(`${this.thumbSelector}, ${this.dotSelector}`);
            if (!trigger) return;
            
            e.preventDefault();
            // Get index relative only to other visible items
            const visibleTriggers = Array.from(
                this.container.querySelectorAll(`${trigger.tagName.toLowerCase()}:not(.bobby-hidden)`)
            );
            const index = visibleTriggers.indexOf(trigger);
            
            if (index !== -1) {
                this.goToSlide(index);
            }
        });

        // 2. Touch/Swipe Logic
        this.addTouchSupport();

        // 3. Scroll Monitoring (to sync dots/thumbs while swiping)
        this.addScrollSync();

        // 4. Variant Change Listener
        this.listenVariantChange();

        // 5. Initial Alignment
        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth < 750;
        });
    }

    /**
     * Updates the internal arrays to ignore items hidden by the Liquid/CSS filter.
     */
    updateVisibleElements() {
        this.visibleSlides = Array.from(this.container.querySelectorAll(`${this.slideSelector}:not(.bobby-hidden)`));
        this.visibleThumbs = Array.from(this.container.querySelectorAll(`${this.thumbSelector}:not(.bobby-hidden)`));
        this.visibleDots = Array.from(this.container.querySelectorAll(`${this.dotSelector}:not(.bobby-hidden)`));
    }

    /**
     * Navigates to a slide based on its visible index.
     */
    goToSlide(index, smooth = true) {
        this.updateVisibleElements();
        
        // Boundary Check
        if (index < 0 || index >= this.visibleSlides.length) return;

        this.currentSlide = index;
        const targetSlide = this.visibleSlides[index];

        // Ensure the container scrolls to the specific element
        targetSlide.scrollIntoView({
            behavior: smooth ? 'smooth' : 'auto',
            block: 'nearest',
            inline: 'start'
        });

        this.updateActiveUI();
    }

    /**
     * Syncs classes for Dots and Thumbnails.
     */
    updateActiveUI() {
        // Sync Thumbs
        this.visibleThumbs.forEach((thumb, idx) => {
            thumb.classList.toggle('bobby-active', idx === this.currentSlide);
        });

        // Sync Dots
        this.visibleDots.forEach((dot, idx) => {
            dot.classList.toggle('bobby-active', idx === this.currentSlide);
        });
    }

    /**
     * Mobile-optimized touch support.
     */
    addTouchSupport() {
        let startX = 0;
        let isDragging = false;

        this.galleryContainer.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            isDragging = true;
        }, { passive: true });

        this.galleryContainer.addEventListener('touchend', (e) => {
            if (!isDragging) return;
            const endX = e.changedTouches[0].clientX;
            const diffX = startX - endX;

            // Sensitivity threshold (50px)
            if (Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    this.goToSlide(this.currentSlide + 1); // Next
                } else {
                    this.goToSlide(this.currentSlide - 1); // Prev
                }
            }
            isDragging = false;
        }, { passive: true });
    }

    /**
     * Detects manual scrolls/swipes to update the "Active" thumbnail.
     */
    addScrollSync() {
        let scrollTimeout;
        this.galleryContainer.addEventListener('scroll', () => {
            window.clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                this.updateVisibleElements();
                const containerRect = this.galleryContainer.getBoundingClientRect();
                
                let closestIndex = 0;
                let minDistance = Infinity;

                this.visibleSlides.forEach((slide, idx) => {
                    const distance = Math.abs(slide.getBoundingClientRect().left - containerRect.left);
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestIndex = idx;
                    }
                });

                if (this.currentSlide !== closestIndex) {
                    this.currentSlide = closestIndex;
                    this.updateActiveUI();
                }
            }, 100);
        }, { passive: true });
    }

    /**
     * Listens for Shopify variant selection to reset the gallery.
     */
    listenVariantChange() {
        document.addEventListener('change', (e) => {
            // Standard Shopify variant ID input name
            if (e.target.name === 'id') {
                // Delay slightly to allow the Liquid/CSS filtering script to hide elements
                setTimeout(() => {
                    this.updateVisibleElements();
                    this.goToSlide(0, false); // Reset to first visible image of the new variant
                }, 150);
            }
        });
    }
}

// Global Initialization
document.addEventListener('DOMContentLoaded', () => {
    const initGalleries = () => {
        document.querySelectorAll('.bobby-product-gallery:not([data-bobby-initialized])').forEach(el => {
            new BobbyProductGallery(el);
            el.setAttribute('data-bobby-initialized', 'true');
        });
    };

    initGalleries();
    
    // Support for themes that reload sections dynamically (Theme Editor / Quick View)
    document.addEventListener('shopify:section:load', initGalleries);
});